import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { writingPrompts } from '../../data/writingPrompts';
import { analyzeEssay } from '../../services/writingScoreService';
const Container = styled.div`
  padding: 0;
  background: #f5f5f5;
  min-height: 100vh;
`;

const TestScreen = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  padding: 2rem;
  height: calc(100vh - 4rem);

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

const PromptSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  height: fit-content;
`;

const WritingSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
`;

const WritingArea = styled.textarea`
  width: 100%;
  flex: 1;
  padding: 1.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  line-height: 1.8;
  font-family: 'Times New Roman', Times, serif;
  resize: none;
  background: #fff;
  color: #333;

  &:focus {
    outline: none;
    border-color: #4a90e2;
    box-shadow: 0 0 0 2px rgba(74,144,226,0.2);
  }
`;

const Timer = styled.div`
  position: fixed;
  top: 1rem;
  right: 1rem;
  background: white;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  font-size: 1.2rem;
  font-weight: bold;
  color: ${props => props.timeLeft < 300 ? '#dc3545' : '#333'};
`;

const Button = styled.button`
  padding: 1rem 2rem;
  background: #2c5282;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #2a4365;
  }

  &:disabled {
    background: #a0aec0;
    cursor: not-allowed;
  }
`;

const WordCount = styled.div`
  margin: 1rem 0;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 0.9rem;
  color: ${props => props.isBelow ? '#dc3545' : '#666'};
  text-align: right;
`;

const TopBar = styled.div`
  background: #2c5282;
  color: white;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FeedbackSection = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  h3 {
    color: #2d3748;
    font-size: 1.25rem;
    margin-bottom: 1.5rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #e2e8f0;
  }
`;

const CategoryScore = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: ${props => {
    if (props.score >= 4) return '#f0fff4';
    if (props.score >= 3) return '#fffff0';
    return '#fff5f5';
  }};
  border-radius: 6px;
  margin-bottom: 1rem;

  h4 {
    color: #2d3748;
    margin: 0;
    font-size: 1rem;
  }

  .score {
    font-weight: 600;
    color: ${props => {
      if (props.score >= 4) return '#38a169';
      if (props.score >= 3) return '#d69e2e';
      return '#e53e3e';
    }};
  }

  .feedback {
    font-size: 0.9rem;
    color: #4a5568;
    margin-top: 0.5rem;
  }
`;

const WritingTest = () => {
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [essay, setEssay] = useState('');
  const [timeLeft, setTimeLeft] = useState(30 * 60); // 30 minutes
  const [isScoring, setIsScoring] = useState(false);
  const [score, setScore] = useState(null);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (selectedPrompt && timeLeft > 0 && !showResults) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [selectedPrompt, timeLeft, showResults]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async () => {
    if (essay.trim().split(/\s+/).length < selectedPrompt.requirements.minWords) {
      alert(`Please write at least ${selectedPrompt.requirements.minWords} words.`);
      return;
    }

    setIsScoring(true);
    try {
      if (!navigator.onLine) {
        throw new Error('No internet connection. Please check your connection and try again.');
      }

      const feedback = await analyzeEssay(essay, selectedPrompt.prompt);
      setScore(feedback);
      setShowResults(true);
    } catch (error) {
      console.error('Submission error:', error);
      const errorMessage = error.message.includes('API key') 
        ? 'Configuration error. Please contact support.'
        : error.message || 'Error analyzing essay. Please try again.';
      alert(errorMessage);
    } finally {
      setIsScoring(false);
    }
  };

  const renderResults = () => {
    if (!score || typeof score !== 'object') return null;

    const formattedScore = {
      overall: score.overall || 0,
      categories: score.categories || {
        taskAchievement: 0,
        coherence: 0,
        vocabulary: 0,
        grammar: 0
      },
      strengths: score.strengths || [],
      improvements: score.improvements || [],
      feedback: score.feedback || '',
      vocabularyHighlights: score.vocabularyHighlights || [],
      grammarIssues: score.grammarIssues || []
    };

    return (
      <div>
        <FeedbackSection highlight>
          <h2>Overall Assessment</h2>
          <h3 style={{ 
            fontSize: '2rem', 
            color: formattedScore.overall >= 24 ? '#28a745' : formattedScore.overall >= 18 ? '#ffc107' : '#dc3545' 
          }}>
            {formattedScore.overall}/30
          </h3>
          <p style={{ marginTop: '1rem', lineHeight: '1.6' }}>{formattedScore.feedback}</p>
        </FeedbackSection>

        <FeedbackSection>
          <h3>Detailed Scores</h3>
          {Object.entries(formattedScore.categories).map(([category, value]) => (
            <CategoryScore key={category} score={value}>
              <div>
                <h4 style={{ margin: '0' }}>
                  {category.split(/(?=[A-Z])/).join(' ')}
                </h4>
              </div>
              <div style={{ 
                fontWeight: 'bold',
                color: value >= 4 ? '#28a745' : value >= 3 ? '#ffc107' : '#dc3545'
              }}>
                {value}/5
              </div>
            </CategoryScore>
          ))}
        </FeedbackSection>

        <Button 
          onClick={() => {
            setShowResults(false);
            setScore(null);
          }}
          style={{ marginTop: '2rem' }}
        >
          Return to Essay
        </Button>
      </div>
    );
  };

  return (
    <Container>
      {!selectedPrompt ? (
        <>
          <TopBar>
            <h1>TOEFL iBT® Writing Section</h1>
          </TopBar>
          <div style={{ maxWidth: '1200px', margin: '2rem auto', padding: '0 2rem' }}>
            <h2 style={{ marginBottom: '2rem', color: '#2d3748' }}>Select Your Writing Task</h2>
            <div style={{ display: 'grid', gap: '1.5rem', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
              {writingPrompts.map((prompt) => (
                <div 
                  key={prompt.id}
                  style={{
                    padding: '1.5rem',
                    background: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    ':hover': {
                      transform: 'translateY(-2px)'
                    }
                  }}
                  onClick={() => setSelectedPrompt(prompt)}
                >
                  <h3 style={{ color: '#2c5282', marginBottom: '1rem' }}>{prompt.title}</h3>
                  <p style={{ color: '#4a5568', marginBottom: '1rem' }}>{prompt.prompt}</p>
                  <div style={{ color: '#718096', fontSize: '0.9rem' }}>
                    <div>Time Limit: {prompt.requirements.timeLimit} minutes</div>
                    <div>Minimum Words: {prompt.requirements.minWords}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          <TopBar>
            <h2>TOEFL iBT® Writing Task</h2>
            <Timer timeLeft={timeLeft}>Time Remaining: {formatTime(timeLeft)}</Timer>
          </TopBar>
          <TestScreen>
            {!showResults ? (
              <>
                <PromptSection>
                  <h3 style={{ color: '#2d3748', marginBottom: '1.5rem' }}>{selectedPrompt.title}</h3>
                  <p style={{ lineHeight: '1.8', color: '#4a5568' }}>{selectedPrompt.prompt}</p>
                </PromptSection>
                <WritingSection>
                  <WritingArea
                    value={essay}
                    onChange={(e) => setEssay(e.target.value)}
                    placeholder="Begin typing your response here..."
                    disabled={isScoring}
                  />
                  <WordCount isBelow={essay.trim().split(/\s+/).length < selectedPrompt.requirements.minWords}>
                    Words: {essay.trim().split(/\s+/).length} / {selectedPrompt.requirements.minWords} minimum
                  </WordCount>
                  <Button 
                    onClick={handleSubmit}
                    disabled={isScoring || essay.trim().split(/\s+/).length < selectedPrompt.requirements.minWords}
                  >
                    {isScoring ? 'Analyzing Response...' : 'Submit Response'}
                  </Button>
                </WritingSection>
              </>
            ) : (
              renderResults()
            )}
          </TestScreen>
        </>
      )}
    </Container>
  );
};

export default WritingTest; 